import { AREAS, DISTRICTS } from '@propertypal/shared/src/constants/housePrices';
import useApi from '@propertypal/shared/src/hooks/useApi';
import useDebounce from '@propertypal/shared/src/hooks/useDebounce';
import { MarketTrendsResponse } from '@propertypal/shared/src/types/marketTrends';
import { ELECTORAL_AREA_LINK } from '@propertypal/shared/src/utils/property/chartUtils';
import slugify from '@propertypal/shared/src/utils/slugify';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import MarketTrendsCharts from '@propertypal/web-ui/src/price-tracker/MarketTrendsCharts';
import MarketTrendsPicker from '@propertypal/web-ui/src/price-tracker/MarketTrendsPicker';
import { Heading, LinkText, MidHeading, Text } from '@propertypal/web-ui/src/typography';
import dynamic from 'next/dynamic';
import router from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { FormContainer, MapContainer, PriceTrackerContainer, NiMap, ChartsContainer } from './MarketTrends.style';

/* istanbul ignore next */
const DEBOUNCE_TIME = process?.env?.JEST_WORKER_ID ? 0 : 500;

const ArmaghAreaMap = dynamic<any>(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/abc-electoral-area-map.svg'),
);
const AntrimAreaMap = dynamic<any>(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/an-electoral-areas-map.svg'),
);
const ArdsAreaMap = dynamic<any>(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/and-electoral-areas-map.svg'),
);
const BelfastAreaMap = dynamic<any>(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/bcc-electoral-areas-map.svg'),
);
const CausewayAreaMap = dynamic<any>(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/ccg-electoral-areas-map.svg'),
);
const DerryAreaMap = dynamic<any>(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/dscdc-electoral-areas-map.svg'),
);
const FermanaghAreaMap = dynamic<any>(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/fo-electoral-areas-map.svg'),
);
const LisburnAreaMap = dynamic<any>(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/lcc-electoral-areas-map.svg'),
);
const MidEastAntrimAreaMap = dynamic<any>(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/mea-electoral-areas-map.svg'),
);
const MidUlsterAreaMap = dynamic<any>(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/mu-electoral-areas-map.svg'),
);
const NewryAreaMap = dynamic<any>(
  () => import('@propertypal/shared/src/resources/images/electoral-maps/nmd-electoral-areas-map.svg'),
);

interface MarketTrendsProps {
  marketTrends: MarketTrendsResponse;
  councilArea: string;
  electoralArea: string;
  setCouncilArea: (councilArea: string) => void;
  setElectoralArea: (electoralArea: string) => void;
}

export const getPageTitle = (councilArea: string | null, electoralArea: string | null) => {
  if (electoralArea) {
    return `${AREAS.find((a) => a.id === electoralArea)?.name} House Prices`;
  }

  if (councilArea) {
    return `${DISTRICTS.find((a) => a.id === councilArea)?.name} House Prices`;
  }

  return 'Northern Ireland House Prices';
};

const MarketTrends: React.FC<MarketTrendsProps> = (props) => {
  const theme = useTheme();
  const { councilArea, setCouncilArea, electoralArea, setElectoralArea } = props;
  const [outcode, setOutcode] = useState<string>('');
  const mounted = useRef<boolean>(false);
  const [data, error, loading, fetchMarketTrends] = useApi<MarketTrendsResponse>(
    `/market-trends`,
    false,
    props.marketTrends,
  );
  const showChart = loading || data || error;
  const debounceFetch = useDebounce(fetchMarketTrends, DEBOUNCE_TIME, { leading: false, trailing: true }, []);

  useEffect(() => {
    // only refetch when not initial load
    // can't just check for values existing as values can be cleared, and would therefore not refetch
    if (mounted.current) {
      if (outcode) {
        debounceFetch(`/market-trends?outcode=${encodeURIComponent(outcode)}`);
      } else {
        fetchMarketTrends(`/market-trends?councilArea=${councilArea}&electoralArea=${electoralArea}`);
      }
    }

    mounted.current = true;
  }, [councilArea, electoralArea, outcode]);

  useEffect(() => {
    const mCouncilArea = DISTRICTS.find((a) => a.id === councilArea);
    const mElectoralArea = AREAS.find((a) => a.id === electoralArea);

    if (mCouncilArea && mElectoralArea) {
      router.replace(`/house-prices/${slugify(mCouncilArea.name)}/${slugify(mElectoralArea.name)}`, undefined, {
        shallow: true,
      });
    } else if (mCouncilArea && !electoralArea) {
      router.replace(`/house-prices/${slugify(mCouncilArea.name)}`, undefined, { shallow: true });
    } else {
      router.replace('/northern-ireland-house-prices', undefined, { shallow: true });
    }
  }, [data]);

  return (
    <PriceTrackerContainer data-testid="market-trends">
      <Heading>{getPageTitle(councilArea, electoralArea)}</Heading>
      <Text>
        Whether you&apos;re buying, renting or just exploring your options, understanding the local market is crucial to
        making the right choice. Our latest market data and average prices give you the insights needed to make
        confident, informed property decisions. There are 11 council areas in Northern Ireland. Each council can be
        broken down into electoral areas.
      </Text>

      <FormContainer>
        <MarketTrendsPicker
          electoralArea={electoralArea}
          setElectoralArea={(v) => {
            // clear outcode
            setOutcode('');
            setElectoralArea(v);
          }}
          councilArea={councilArea}
          setCouncilArea={(v) => {
            // clear outcode
            setOutcode('');
            setCouncilArea(v);
          }}
          outcode={outcode}
          setOutcode={(o) => {
            setCouncilArea('');
            setElectoralArea('');
            setOutcode(o);
          }}
        />
        <MapContainer area={electoralArea}>
          {electoralArea ? (
            <>
              {councilArea === 'N09000001' && <AntrimAreaMap data-testid="antrim-map" className="area-map" />}
              {councilArea === 'N09000002' && <ArmaghAreaMap data-testid="armagh-map" className="area-map" />}
              {councilArea === 'N09000003' && <BelfastAreaMap data-testid="belfast-map" className="area-map" />}
              {councilArea === 'N09000004' && <CausewayAreaMap data-testid="causeway-map" className="area-map" />}
              {councilArea === 'N09000005' && <DerryAreaMap data-testid="derry-map" className="area-map" />}
              {councilArea === 'N09000006' && <FermanaghAreaMap data-testid="fermanagh-map" className="area-map" />}
              {councilArea === 'N09000007' && <LisburnAreaMap data-testid="lisburn-map" className="area-map" />}
              {councilArea === 'N09000008' && (
                <MidEastAntrimAreaMap data-testid="mid-east-antrim-map" className="area-map" />
              )}
              {councilArea === 'N09000009' && <MidUlsterAreaMap data-testid="mid-ulster-map" className="area-map" />}
              {councilArea === 'N09000010' && <NewryAreaMap data-testid="newry-map" className="area-map" />}
              {councilArea === 'N09000011' && <ArdsAreaMap data-testid="ards-map" className="area-map" />}
            </>
          ) : (
            <NiMap district={councilArea || data?.coucilArea?.id} data-testid="ni-map" />
          )}
        </MapContainer>
      </FormContainer>

      {data && showChart && (
        <ChartsContainer data-testid="market-trends-charts">
          <MarketTrendsCharts
            data={data}
            loading={loading}
            error={error}
            councilArea={!!councilArea}
            electoralArea={!!electoralArea}
            outcode={!!outcode}
          />
        </ChartsContainer>
      )}

      {!showChart && (
        <div data-testid="no-selection">
          <PageWrapper
            containerStyle={{ backgroundColor: theme.backgroundLightest, padding: '100px 15px', marginTop: 80 }}
          >
            <MidHeading align="center" maxWidth={650} autoMargin fontSize={18} fontWeight="normal">
              Select a council and electoral (e.g. Belfast &gt; Titanic) from the dropdowns above to view market trends
              in your specific location.
            </MidHeading>
          </PageWrapper>
        </div>
      )}

      <Text fontSize={12} fontWeight="800" mt={60}>
        DISCLAIMER
      </Text>

      <Text fontSize={12} mb={15}>
        Unless otherwise stated, all content in this section is copyright of PropertyPal 2019, and all rights are
        reserved. The contents of this section, either in whole or in part, may not be reproduced, stored or transmitted
        without prior written permission from PropertyPal. This section has been prepared by PropertyPal for general
        guidance on matters of interest only, and does not constitute professional advice. You should not act upon the
        information contained in this section without obtaining specific professional advice. No representation or
        warranty (express or implied) is given as to the accuracy or completeness of the information contained in this
        section. It is a condition of PropertyPal allowing you free access to this section that you accept that
        PropertyPal, its clients, employees and agents do not accept or assume any liability, responsibility or duty of
        care for any consequences of you or anyone else acting, or refraining to act, in reliance on the information
        contained in this section or for any decision based on it.
      </Text>

      <Text fontSize={12} mb={60}>
        *Note: The dataset is compiled by PropertyPal with statistical analyses performed using listed price information
        which has been cleansed to remove anomalies. Locations provided are based on the latest electoral area
        boundaries provided by the District Electoral Areas Commissioner and can be accessed{' '}
        <LinkText href={ELECTORAL_AREA_LINK} target="_blank" rel="noreferrer">
          here
        </LinkText>
        .
      </Text>
    </PriceTrackerContainer>
  );
};

export default MarketTrends;
